// istanbul ignore file
import { z } from 'zod';
import {
    startOfDay,
    isAfter,
    isBefore,
    parseISO,
    format,
    isMatch,
} from 'date-fns';
// eslint-disable-next-line import/no-cycle
import { IntegratedServiceManager, GAS_APPLIANCE_VALUES } from './portfolio';

// Error / message string constants
const DATE_FORMAT_ERROR = 'Please select or enter a valid date (dd/mm/yyyy)';
const ERROR_LAST_FUTURE = 'Last service date cannot be in the future';
const ERROR_NEXT_AFTER_LAST = 'Next service date must be on or after the last service date';
const ERROR_NEXT_PAST_NEEDS_LAST = 'Next service date in the past requires a last service date';

// Preprocess to canonical date-only string (yyyy-MM-dd) or null.
// Input domain we actually expect now: string | null coming from form or hydration.
// Still gracefully handle Date (legacy) and unknown types.
function coerceDateString(val: unknown): string | null {
    if (val === null || val === undefined || val === '') return null;
    if (val instanceof Date) return format(val, 'yyyy-MM-dd');
    if (typeof val === 'string') {
        const t = val.trim();
        return t === '' ? null : t;
    }
    // Force failure via regex mismatch so we surface unified DATE_FORMAT_ERROR
    return '§INVALID§';
}

const dateStringField = z.preprocess(
    coerceDateString,
    z.union([
        z
            .string()
            .refine((s) => isMatch(s, 'yyyy-MM-dd'), DATE_FORMAT_ERROR),
        z.null(),
    ])
);

// A single service's compliance-relevant fields (expand later as needed)
const baseServiceSchema = z.object({
    complianceId: z.number().nullable().optional().default(null),
    complianceRequirementId: z.number().default(0),
    name: z.string().default(''),
    shortName: z.string().default(''),
    managedBy: z.enum([
        IntegratedServiceManager.Landlord,
        IntegratedServiceManager.ThirdParty,
        IntegratedServiceManager.Unknown,
        IntegratedServiceManager.NotManaged,
    ]).nullable(),
    lastServiceDate: dateStringField.default(''),
    nextServiceDate: dateStringField.default(''),
    isFurtherWorksRequired: z.number().nullable(),
    providedBy: z.object({
        id: z.coerce.number().nullable(),
        providedBy: z.string().nullable(),
    }).optional(),
    serviceFrequencyMonths: z.number().int().positive().optional(),
});

export const serviceSchema = baseServiceSchema.superRefine((vals, ctx) => {
    const today = startOfDay(new Date());
    const toDate = (s: string | null) => {
        if (s === null) return null;
        if (s === '') return null;
        return startOfDay(parseISO(s));
    };

    const last = toDate(vals.lastServiceDate);
    const next = toDate(vals.nextServiceDate);

    if (last !== null && isAfter(last, today)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ERROR_LAST_FUTURE,
            path: ['lastServiceDate'],
        });
    }

    if (last && next) {
        // Require not before (allow equal)
        if (isBefore(next, last)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: ERROR_NEXT_AFTER_LAST,
                path: ['nextServiceDate'],
            });
        }
    }

    if (next !== null) {
        if (isBefore(next, today) && last === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: ERROR_NEXT_PAST_NEEDS_LAST,
                path: ['nextServiceDate'],
            });
        }
    }
});

const GasApplianceCountSchema = z.union([
    z.literal(GAS_APPLIANCE_VALUES.NONE),
    z.literal(GAS_APPLIANCE_VALUES.ONE),
    z.literal(GAS_APPLIANCE_VALUES.TWO_OR_MORE),
    z.literal(GAS_APPLIANCE_VALUES.UNKNOWN_COUNT),
    z.null(),
    z.undefined(),
]);

const manualComplianceDocumentSchema = z.object({
    id: z.string(),
});

const propertySchema = z.object({
    portfolioPropertyId: z.number().default(0),
    gasApplianceCountValue: GasApplianceCountSchema.default(null),
    services: z.array(serviceSchema),
    documents: z.record(z.array(manualComplianceDocumentSchema)),
}).passthrough(); // allow extra fields (counts etc.) for now

const editComplianceSchema = z.object({
    properties: z.record(propertySchema),
});

type EditComplianceFormValues = z.infer<typeof editComplianceSchema>;

type EditComplianceFormProperty = z.infer<typeof propertySchema>;

export {
    DATE_FORMAT_ERROR,
    EditComplianceFormValues,
    EditComplianceFormProperty,
    editComplianceSchema,
    propertySchema,
    GasApplianceCountSchema,
};
